import React, { useCallback, useRef, type KeyboardEvent, useEffect } from 'react';
import { Box, xcss } from '@atlaskit/primitives';
import type { EntryPointReferenceSubject } from '@atlassian/jira-entry-point';
import { ff } from '@atlassian/jira-feature-flagging';
import IssueCreateForm from '../../../common/ui/issue-create-form';
import type ContextFieldsEntryPoint from '../../../common/ui/issue-create-form/context-fields/entrypoint';
import { IssueCreateFormContainer } from '../../../controllers/issue-create-form';

type Props = {
	/**
	 * Entry point reference subject for context fields in the issue create form, i.e. issue type. This allows
	 * preloading of project and issue type data on user intent to open the form. The parent is responsible for ensuring
	 * an appropriate trigger is defined to load this entry point.
	 */
	contextFieldsSubject: EntryPointReferenceSubject<typeof ContextFieldsEntryPoint>;
	/**
	 * Event emitted when the issue create footer should be closed.
	 * @param isKeyboard `true` if the close event was initiated from a keyboard action.
	 */
	onClose: (isKeyboard: boolean) => void;
};

export const IssueCreateFooter = ({ contextFieldsSubject, onClose }: Props) => {
	const issueCreateFooterRef = useRef<HTMLElement | null>(null);

	const onKeyDown = useCallback(
		(event: KeyboardEvent<HTMLDivElement>) => {
			if (event.key === 'Escape') {
				onClose(true);
			}
		},
		[onClose],
	);

	const onClickOutside = useCallback(
		(event: MouseEvent) => {
			const clickInside =
				event.target instanceof Node &&
				(issueCreateFooterRef.current?.contains(event.target) ?? false);
			!clickInside && onClose(false);
		},
		[onClose],
	);

	useEffect(() => {
		document.body.addEventListener('click', onClickOutside, true);
		return () => document.body.removeEventListener('click', onClickOutside, true);
	}, [onClickOutside]);

	return (
		<Box
			paddingBlock={
				ff('jsv-one-issue-table-sticky-compact-footer_657cf') ? 'space.075' : 'space.200'
			}
			xcss={[
				footerStyles,

				!ff('jsv-one-issue-table-spreadsheet-ui_ygrnf') && borderStyles,

				ff('jsv-one-issue-table-sticky-compact-footer_657cf') && stickyCompactFooterStyles,
			]}
			onKeyDown={onKeyDown}
			ref={issueCreateFooterRef}
		>
			<IssueCreateFormContainer>
				<IssueCreateForm contextFieldsSubject={contextFieldsSubject} />
			</IssueCreateFormContainer>
		</Box>
	);
};

const footerStyles = xcss({
	position: 'sticky',
	left: '0',
});

const borderStyles = xcss({
	borderColor: 'color.border',
	borderTopWidth: 'border.width.outline',
	borderTopStyle: 'solid',
});

const stickyCompactFooterStyles = xcss({
	bottom: '0',
	backgroundColor: 'elevation.surface',
});
